import { useEffect, useState, useRef, useCallback, useMemo, type ComponentProps } from 'react';
import { useReviewSession, buildInitialDecisions, type ReviewDecisions, type DecisionAction } from '@/hooks/useReviewSession';
import { Link, useLocation } from 'react-router-dom';
import { SeekLayout } from '@/components/seek/SeekLayout';
import { useCandidateProfile, type CandidateProfileTable } from '@/hooks/useCandidateProfile';
import { PersonalDetailsSection } from '@/components/seek/profile/PersonalDetailsSection';
import { ExperienceSection } from '@/components/seek/profile/ExperienceSection';
import { EducationSection } from '@/components/seek/profile/EducationSection';
import { SkillsSection } from '@/components/seek/profile/SkillsSection';
import { LanguagesSection } from '@/components/seek/profile/LanguagesSection';
import { CertificationsSection } from '@/components/seek/profile/CertificationsSection';
import { LinksSection } from '@/components/seek/profile/LinksSection';
import { CvDiffPreview } from '@/components/seek/profile/CvDiffPreview';
import { PreferencesSection } from '@/components/seek/profile/PreferencesSection';
import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Loader2, Upload, FileText, CheckCircle2, AlertTriangle, Sparkles, Brain, Zap, Shield, Target } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useTranslation } from 'react-i18next';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import { useSeekAuth } from '@/contexts/SeekAuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ingestForkSeekCv,
  SEEK_CV_FILE_ACCEPT,
  validateSeekCvFile,
} from '@/lib/seekCvIngestion';

type CvDiffPreviewProps = ComponentProps<typeof CvDiffPreview>;
type CvParsedData = CvDiffPreviewProps['parsedData'];
type CvValidationIssue = CvDiffPreviewProps['validationIssues'][number];
type CvExperience = NonNullable<CvParsedData['experiences']>[number];
type CvEducation = NonNullable<CvParsedData['education']>[number];
type CvCertification = NonNullable<CvParsedData['certifications']>[number];
type CvLanguage = NonNullable<CvParsedData['languages']>[number];
type CvAdditionalSection = NonNullable<CvParsedData['additional_sections']>[number];

interface CvImportDiff {
  parsed_data: CvParsedData;
  confidence: Record<string, string>;
  extraction_quality: string;
  validation_issues: CvValidationIssue[];
  import_id?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readStringField(value: unknown, field: string): string | undefined {
  if (!isRecord(value)) return undefined;
  const fieldValue = value[field];
  return typeof fieldValue === 'string' ? fieldValue : undefined;
}

function isOptionalNullableString(value: unknown): value is string | null | undefined {
  return value === undefined || value === null || typeof value === 'string';
}

function isOptionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === 'string';
}

function isOptionalNullableNumber(value: unknown): value is number | null | undefined {
  return value === undefined || value === null || typeof value === 'number';
}

function isOptionalNullableBoolean(value: unknown): value is boolean | null | undefined {
  return value === undefined || value === null || typeof value === 'boolean';
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function isStringRecord(value: unknown): value is Record<string, string> {
  return isRecord(value) && Object.values(value).every((item) => typeof item === 'string');
}

function isCvExperience(value: unknown): value is CvExperience {
  return isRecord(value)
    && typeof value.title === 'string'
    && isOptionalNullableString(value.employer_name)
    && isOptionalNullableString(value.location)
    && isOptionalNullableString(value.employment_type)
    && isOptionalNullableString(value.start_date)
    && isOptionalNullableString(value.end_date)
    && isOptionalNullableBoolean(value.is_current)
    && isOptionalNullableString(value.description);
}

function isCvEducation(value: unknown): value is CvEducation {
  return isRecord(value)
    && typeof value.institution_name === 'string'
    && isOptionalNullableString(value.degree)
    && isOptionalNullableString(value.field_of_study)
    && isOptionalNullableString(value.start_date)
    && isOptionalNullableString(value.end_date);
}

function isCvCertification(value: unknown): value is CvCertification {
  return isRecord(value)
    && typeof value.certification_name === 'string'
    && isOptionalNullableString(value.issuing_organization);
}

function isCvLanguage(value: unknown): value is CvLanguage {
  return isRecord(value)
    && typeof value.language_name === 'string'
    && isOptionalNullableString(value.proficiency_level);
}

function isCvAdditionalSection(value: unknown): value is CvAdditionalSection {
  return isRecord(value)
    && typeof value.stable_id === 'string'
    && typeof value.section_label === 'string'
    && typeof value.category === 'string'
    && typeof value.content === 'string'
    && typeof value.confidence === 'number'
    && typeof value.quality === 'string';
}

function isCvSkills(value: unknown): value is CvParsedData['skills'] {
  if (isStringArray(value)) return true;
  return isRecord(value)
    && (value.skills === undefined || isStringArray(value.skills))
    && (value.expertise === undefined || isStringArray(value.expertise))
    && (value.key_strengths === undefined || isStringArray(value.key_strengths));
}

function isCvParsedData(value: unknown): value is CvParsedData {
  if (!isRecord(value) || typeof value.extraction_quality !== 'string') return false;
  return (value.confidence === undefined || isStringRecord(value.confidence))
    && isOptionalNullableString(value.headline)
    && isOptionalNullableString(value.professional_summary)
    && isOptionalNullableString(value.current_job_title)
    && isOptionalNullableNumber(value.total_years_experience)
    && isOptionalNullableString(value.phone)
    && isOptionalNullableString(value.city)
    && isOptionalNullableString(value.country)
    && isOptionalNullableString(value.nationality)
    && isOptionalNullableString(value.highest_education_level)
    && (value.experiences === undefined || (Array.isArray(value.experiences) && value.experiences.every(isCvExperience)))
    && (value.education === undefined || (Array.isArray(value.education) && value.education.every(isCvEducation)))
    && (value.skills === undefined || isCvSkills(value.skills))
    && (value.certifications === undefined || (Array.isArray(value.certifications) && value.certifications.every(isCvCertification)))
    && (value.languages === undefined || (Array.isArray(value.languages) && value.languages.every(isCvLanguage)))
    && (value.additional_sections === undefined || (Array.isArray(value.additional_sections) && value.additional_sections.every(isCvAdditionalSection)));
}

function isCvValidationIssue(value: unknown): value is CvValidationIssue {
  if (!isRecord(value)) return false;
  return typeof value.section === 'string'
    && isOptionalString(value.field)
    && typeof value.issue === 'string'
    && (value.severity === 'error' || value.severity === 'warning' || value.severity === 'info')
    && (value.auto_fixed === undefined || typeof value.auto_fixed === 'boolean');
}

function buildCvImportDiff(value: unknown): CvImportDiff {
  const source = isRecord(value) ? value : {};
  const extractionQuality = readStringField(source, 'extraction_quality') ?? 'unknown';
  const parsedCandidate = isRecord(source.parsed_data)
    ? { ...source.parsed_data, extraction_quality: readStringField(source.parsed_data, 'extraction_quality') ?? extractionQuality }
    : { extraction_quality: extractionQuality };

  return {
    parsed_data: isCvParsedData(parsedCandidate) ? parsedCandidate : { extraction_quality: extractionQuality },
    confidence: isStringRecord(source.confidence) ? source.confidence : {},
    extraction_quality: extractionQuality,
    validation_issues: Array.isArray(source.validation_issues)
      ? source.validation_issues.filter(isCvValidationIssue)
      : [],
    import_id: readStringField(source, 'import_id'),
  };
}

function getErrorMessage(error: unknown, fallback: string): string {
  return readStringField(error, 'message') ?? fallback;
}

export default function SeekProfile() {
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useSeekAuth();
  const qc = useQueryClient();
  const [exporting, setExporting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [diffPreviewOpen, setDiffPreviewOpen] = useState(false);
  const [diffData, setDiffData] = useState<CvImportDiff | null>(null);
  const diffDataRef = useRef<CvImportDiff | null>(null);
  const [diffDocId, setDiffDocId] = useState<string | null>(null);
  const [diffStorageKey, setDiffStorageKey] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const reviewSession = useReviewSession();
  const profileDecisions = reviewSession.decisions;
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const profileRetryAttemptedRef = useRef(false);
  const [parseProgress, setParseProgress] = useState(0);
  const [parseMessageIdx, setParseMessageIdx] = useState(0);

  const parseMessages = useMemo(() => [
    { icon: Upload, text: t('seek.profileImport.msg1'), sub: t('seek.profileImport.sub1') },
    { icon: Shield, text: t('seek.profileImport.msg2'), sub: t('seek.profileImport.sub2') },
    { icon: Brain, text: t('seek.profileImport.msg3'), sub: t('seek.profileImport.sub3') },
    { icon: Sparkles, text: t('seek.profileImport.msg4'), sub: t('seek.profileImport.sub4') },
    { icon: Target, text: t('seek.profileImport.msg5'), sub: t('seek.profileImport.sub5') },
    { icon: Zap, text: t('seek.profileImport.msg6'), sub: t('seek.profileImport.sub6') },
  ], [t]);

  // Simulated progress during parsing
  useEffect(() => {
    if (!uploading) {
      setParseProgress(0);
      setParseMessageIdx(0);
      return;
    }
    // Progress simulation: fast to 30, slow to 85, stall there
    const interval = setInterval(() => {
      setParseProgress(prev => {
        if (prev < 30) return prev + 3;
        if (prev < 60) return prev + 1.5;
        if (prev < 85) return prev + 0.5;
        return prev;
      });
    }, 200);
    // Rotate messages every 4 seconds
    const msgInterval = setInterval(() => {
      setParseMessageIdx(prev => (prev + 1) % parseMessages.length);
    }, 4000);
    return () => { clearInterval(interval); clearInterval(msgInterval); };
  }, [uploading, parseMessages]);
  const {
    profile, profileLoading, profileError, refetchProfile, experiences, education, skills,
    languages, certifications, links, updateProfile, addRecord,
    updateRecord, deleteRecord, profileId,
  } = useCandidateProfile();

  useEffect(() => {
    diffDataRef.current = diffData;
  }, [diffData]);

  useEffect(() => {
    if (!user) {
      profileRetryAttemptedRef.current = false;
      return;
    }

    if (profile) {
      profileRetryAttemptedRef.current = false;
      return;
    }

    if (!profileLoading && !profileRetryAttemptedRef.current) {
      profileRetryAttemptedRef.current = true;
      refetchProfile();
    }
  }, [user, profile, profileLoading, refetchProfile]);

  // Auto-open review dialog when navigated with openCvReview flag or when session recovery finds an active session
  useEffect(() => {
    if (!profile || profileLoading) return;
    const state = location.state as { openCvReview?: boolean } | null;
    const hasActiveReviewSession = reviewSession.hasActiveSession && reviewSession.isRecoveredSession;

    if (state?.openCvReview || hasActiveReviewSession) {
      // If there's a recovered session with an import, load the import data and open diff preview
      const importId = reviewSession.importId;
      if (importId && !diffDataRef.current) {
        seekSupabase
          .from('candidate_profile_imports')
          .select('*')
          .eq('id', importId)
          .single()
          .then(({ data: importRow }) => {
            if (importRow?.normalized_payload_json) {
              const normalized: unknown = typeof importRow.normalized_payload_json === 'string'
                ? JSON.parse(importRow.normalized_payload_json)
                : importRow.normalized_payload_json;
              if (isRecord(normalized)) {
                setDiffData(buildCvImportDiff({
                  parsed_data: normalized,
                  confidence: importRow.confidence_json,
                  extraction_quality: readStringField(normalized, 'extraction_quality') ?? 'unknown',
                  validation_issues: [],
                  import_id: importId,
                }));
                setDiffDocId(importRow.source_reference || null);
                setDiffPreviewOpen(true);
              }
            }
          });
      }
      // Clear the navigation state
      if (state?.openCvReview) {
        window.history.replaceState({}, document.title);
      }
    }
  }, [profile, profileLoading, reviewSession.hasActiveSession, reviewSession.isRecoveredSession, reviewSession.importId, location.state]);

  const handleExportCv = async () => {
    setExporting(true);
    try {
      const { data: { session } } = await seekSupabase.auth.getSession();
      if (!session) { toast.error(t('seek.profileImport.signInRequired')); return; }

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/export-cv`,
        { headers: { Authorization: `Bearer ${session.access_token}`, apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY } }
      );
      if (!res.ok) {
        const errorBody: unknown = await res.json().catch(() => ({}));
        throw new Error(readStringField(errorBody, 'error') ?? 'Export failed');
      }

      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match?.[1] || 'CV.pdf';

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success(t('seek.profileImport.cvDownloaded'));
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, t('seek.profileImport.exportFailed')));
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    e.target.value = '';

    const validation = validateSeekCvFile(file);
    if (validation.valid === false) {
      toast.error(t(validation.code === 'file_too_large'
        ? 'seek.profileImport.fileTooLarge'
        : 'seek.profileImport.onlyPdfImage'));
      return;
    }

    // Check if there's already a document being processed
    const { data: existingDocs } = await seekSupabase
      .from('candidate_documents')
      .select('id')
      .eq('candidate_id', user.id)
      .in('parse_status', ['pending', 'processing']);
    if (existingDocs && existingDocs.length > 0) {
      toast.error(t('seek.profileImport.alreadyProcessing'));
      return;
    }

    // Check if profile already has meaningful data — ask for confirmation
    const profileHasData = !!(
      profile?.current_job_title ||
      profile?.professional_summary ||
      experiences.length > 0 ||
      education.length > 0 ||
      skills.length > 0
    );

    if (profileHasData) {
      setPendingFile(file);
      setConfirmOverwrite(true);
      return;
    }

    // No existing data, proceed directly
    await processUpload(file);
  };

  const processUpload = async (file: File) => {
    if (!user) return;
    setUploading(true);
    try {
      toast.info(t('seek.profileImport.uploadedParsing'));
      const ingestion = await ingestForkSeekCv({ candidateId: user.id, file, source: 'profile_import' });
      const res = ingestion.parsedData;
      const importDiff = buildCvImportDiff(res);

      // Step 2: Show diff preview for candidate approval
      setDiffData(importDiff);
      setDiffDocId(ingestion.documentId);
      setDiffStorageKey(ingestion.storageKey);
      // Initialize decisions for the profile merge-as-is flow
      const initialDec = buildInitialDecisions(
        importDiff.parsed_data,
        { profile, experiences, education, skills, languages, certifications },
        importDiff.confidence,
      );

      // Find the import ID from the parse result
      const parseImportId = importDiff.import_id;
      if (parseImportId) {
        await reviewSession.startSession.mutateAsync({ importId: parseImportId, initialDecisions: initialDec });
      } else {
        // Fallback: set decisions directly (legacy path)
        reviewSession.setDecisions(initialDec);
      }
      setDiffPreviewOpen(true);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : t('seek.profileImport.parseFailed'));
    } finally {
      setUploading(false);
    }
  };

  const applyParsedData = useCallback(async (editedData?: CvParsedData, appliedDecisions?: ReviewDecisions) => {
    if (!user || !diffData?.parsed_data) return;
    setApplying(true);
    try {
      const { data: { session } } = await seekSupabase.auth.getSession();
      if (!session) throw new Error(t('seek.profileImport.notAuthenticated'));

      // Determine import_id: from review session (preferred) or from diffData
      const importId = reviewSession.importId || diffData?.import_id;
      if (!importId) throw new Error(t('seek.profileImport.noImportRecord'));

      // Route through sync-cv-to-profile which respects decisions and applies
      // authoritative override for accepted sections
      const syncRes = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-cv-to-profile`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({
            import_id: importId,
            decisions: appliedDecisions || reviewSession.decisions,
            edited_data: editedData,
          }),
        }
      );

      if (!syncRes.ok) {
        const errorBody: unknown = await syncRes.json().catch(() => ({}));
        throw new Error(readStringField(errorBody, 'error') ?? 'Sync failed');
      }

      qc.invalidateQueries({ queryKey: ['candidate-profile'] });
      qc.invalidateQueries({ queryKey: ['candidate-experiences'] });
      qc.invalidateQueries({ queryKey: ['candidate-education'] });
      qc.invalidateQueries({ queryKey: ['candidate-skills'] });
      qc.invalidateQueries({ queryKey: ['candidate-languages'] });
      qc.invalidateQueries({ queryKey: ['candidate-certifications'] });
      qc.invalidateQueries({ queryKey: ['candidate-links'] });

      toast.success(t('seek.profileImport.profileUpdated'));

      // Complete the review session
      await reviewSession.completeSession.mutateAsync();

      setDiffPreviewOpen(false);
      setDiffData(null);
      setDiffDocId(null);
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, t('seek.profileImport.applyFailed')));
    } finally {
      setApplying(false);
    }
  }, [user, diffData, qc, reviewSession, t]);

  // Soft close — just hide the dialog, keep data so user can return
  const handleDiffClose = useCallback(() => {
    setDiffPreviewOpen(false);
  }, []);

  // Hard discard — clean up uploaded doc, discard review session, clear state
  const handleDiffDiscard = useCallback(async () => {
    // Phase 1 safety: do NOT delete CV documents — retain for 12 months
    await reviewSession.discardSession.mutateAsync().catch(() => {});
    setDiffPreviewOpen(false);
    setDiffData(null);
    setDiffDocId(null);
    setDiffStorageKey(null);
  }, [reviewSession]);

  const saving = updateProfile.isPending || addRecord.isPending || updateRecord.isPending || deleteRecord.isPending;

  // Compute profile completion with named checks
  const { completionScore, missingItems } = (() => {
    if (!profile) return { completionScore: 0, missingItems: [] as { label: string; sectionId: string; editId?: string }[] };
    const items: { label: string; done: boolean; sectionId: string; editId?: string }[] = [
      // Personal details
      { label: t('seek.profile.currentJobTitle'), done: !!profile?.current_job_title, sectionId: 'section-personal' },
      { label: t('seek.profile.professionalSummary'), done: !!profile?.professional_summary, sectionId: 'section-personal' },
      { label: t('seek.profile.phone'), done: !!profile?.phone, sectionId: 'section-personal' },
      { label: t('seek.profile.country'), done: !!profile?.country, sectionId: 'section-personal' },
      { label: t('seek.profile.nationality'), done: !!profile?.nationality, sectionId: 'section-personal' },
      { label: t('seek.profile.profilePhoto'), done: !!profile?.avatar_url, sectionId: 'section-personal', editId: 'avatar' },
      // Sections
      { label: skills.length >= 5 ? t('seek.profile.skills') + ' (5+)' : `${t('seek.profile.skills')} (${skills.length}/5)`, done: skills.length >= 5, sectionId: 'section-skills' },
      { label: t('seek.profileImport.atLeastOneExp'), done: experiences.length > 0 || !!profile?.is_fresh_graduate, sectionId: 'section-experience' },
      { label: t('seek.profileImport.atLeastOneEdu'), done: education.length > 0, sectionId: 'section-education' },
      { label: t('seek.profileImport.atLeastOneLang'), done: languages.length > 0, sectionId: 'section-languages' },
    ];

    // Experience detail checks (skip if fresh graduate)
    if (!profile?.is_fresh_graduate && experiences.length > 0) {
      const expSection = t('seek.profile.experience');
      experiences.forEach((exp) => {
        items.push({ label: `${expSection} – ${t('seek.profile.startDate')}`, done: !!exp.start_date, sectionId: 'section-experience', editId: exp.id });
        items.push({ label: `${expSection} – ${t('seek.profile.endDate')}`, done: !!exp.end_date || !!exp.is_current, sectionId: 'section-experience', editId: exp.id });
        items.push({ label: `${expSection} – ${t('seek.profile.employerName')}`, done: !!exp.employer_name, sectionId: 'section-experience', editId: exp.id });
        items.push({ label: `${expSection} – ${t('seek.profile.employmentType')}`, done: !!exp.employment_type, sectionId: 'section-experience', editId: exp.id });
        items.push({ label: `${expSection} – ${t('seek.profile.location')}`, done: !!exp.location, sectionId: 'section-experience', editId: exp.id });
        items.push({ label: `${expSection} – ${t('seek.profile.description')}`, done: !!exp.description, sectionId: 'section-experience', editId: exp.id });
      });
    }

    // Education detail checks
    if (education.length > 0) {
      const eduSection = t('seek.profile.education');
      education.forEach((edu) => {
        items.push({ label: `${eduSection} – ${t('seek.profile.degree')}`, done: !!edu.degree, sectionId: 'section-education', editId: edu.id });
        items.push({ label: `${eduSection} – ${t('seek.profile.fieldOfStudy')}`, done: !!edu.field_of_study, sectionId: 'section-education', editId: edu.id });
        items.push({ label: `${eduSection} – ${t('seek.profile.startDate')}`, done: !!edu.start_date, sectionId: 'section-education', editId: edu.id });
        items.push({ label: `${eduSection} – ${t('seek.profile.endDate')}`, done: !!edu.end_date, sectionId: 'section-education', editId: edu.id });
        items.push({ label: `${eduSection} – ${t('seek.profile.location')}`, done: !!edu.location, sectionId: 'section-education', editId: edu.id });
      });
    }

    const score = Math.round((items.filter(i => i.done).length / items.length) * 100);
    const missing = items.filter(i => !i.done).map(i => ({ label: i.label, sectionId: i.sectionId, editId: i.editId }));
    return { completionScore: score, missingItems: missing };
  })();


  if (profileLoading) {
    return (
      <SeekLayout>
        <div className="space-y-5 sm:space-y-6 max-w-4xl mx-auto">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-40 w-full rounded-xl" />)}
        </div>
      </SeekLayout>
    );
  }

  if (!profile) {
    return (
      <SeekLayout>
        <Card className="max-w-2xl mx-auto rounded-xl p-6 space-y-3">
          <h2 className="text-lg font-semibold text-foreground">{t('seek.profile.loadFailedTitle')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('seek.profile.loadFailedDesc')}
          </p>
          {profileError instanceof Error && (
            <p className="text-sm text-destructive">{profileError.message}</p>
          )}
          <div>
            <Button
              variant="outline"
              onClick={() => {
                profileRetryAttemptedRef.current = false;
                refetchProfile();
              }}
            >
              {t('seek.profile.retryLoading')}
            </Button>
          </div>
        </Card>
      </SeekLayout>
    );
  }

  const handleAddRecord = (table: CandidateProfileTable) => (data: Record<string, unknown>) => {
    addRecord.mutate({ table, data });
  };
  const handleUpdateRecord = (table: CandidateProfileTable) => (id: string, data: Record<string, unknown>) => {
    updateRecord.mutate({ table, id, data });
  };
  const handleDeleteRecord = (table: CandidateProfileTable) => (id: string) => {
    deleteRecord.mutate({ table, id });
  };

  return (
    <SeekLayout>
      <div className="space-y-5 sm:space-y-6 lg:space-y-8 max-w-[1200px] 3xl:max-w-[1440px] 4xl:max-w-[1600px] mx-auto">
        {/* Page header */}
        <Card className="rounded-xl p-4 sm:p-5 lg:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground font-[var(--font-display)]">
              {t('seek.profile.title')}
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              {t('seek.profile.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 hover:bg-seek/10 hover:text-seek hover:border-seek"
              onClick={() => setImportDialogOpen(true)}
              disabled={uploading}
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? t('seek.profileImport.parsing') : t('seek.profileImport.importCv')}
            </Button>

            {/* Import CV Dialog */}
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-lg">
                    <div className="w-9 h-9 rounded-xl bg-seek/10 flex items-center justify-center">
                      <Upload className="w-5 h-5 text-seek" />
                    </div>
                    {t('seek.profileImport.importDialogTitle')}
                  </DialogTitle>
                  <DialogDescription className="text-muted-foreground pt-1">
                    {t('seek.profileImport.importDialogDesc')}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 pt-2">
                  {/* Instructions */}
                  <div className="space-y-2.5">
                    {[
                      t('seek.profileImport.step1'),
                      t('seek.profileImport.step2'),
                      t('seek.profileImport.step3'),
                      t('seek.profileImport.step4'),
                    ].map((text, i) => (
                      <div key={i} className="flex items-start gap-2.5 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-seek shrink-0 mt-0.5" />
                        <span className="text-foreground/80">{text}</span>
                      </div>
                    ))}
                  </div>

                  {/* Accepted formats */}
                  <div className="rounded-lg bg-muted/50 border border-border p-3">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">{t('seek.profileImport.acceptedFormats')}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {['PDF', 'DOCX', 'JPG', 'PNG', 'WebP'].map((fmt) => (
                        <span key={fmt} className="inline-flex items-center gap-1 text-xs font-medium bg-background border border-border rounded-md px-2 py-1">
                          <FileText className="w-3 h-3 text-muted-foreground" />
                          {fmt}
                        </span>
                      ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1.5">{t('seek.profileImport.maxFileSize')}</p>
                  </div>

                  {/* AI processing consent notice */}
                  <div className="p-3 rounded-lg bg-muted/30 border border-border text-xs text-muted-foreground leading-relaxed">
                    {t('seek.profileImport.aiProcessingNotice')}{' '}
                    <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary font-medium underline underline-offset-2 hover:text-primary/80">
                      {t('seek.profileImport.privacyPolicy')}
                    </Link>
                  </div>

                  {/* Upload button */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={SEEK_CV_FILE_ACCEPT}
                    className="hidden"
                    onChange={(e) => {
                      setImportDialogOpen(false);
                      handleFileSelected(e);
                    }}
                  />
                  <Button
                    className="w-full gap-2 bg-seek hover:bg-seek/90 text-seek-foreground"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading ? t('seek.profileImport.parsing') : t('seek.profileImport.chooseFile')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Parsing Progress Overlay */}
            <Dialog open={uploading} onOpenChange={() => {}}>
              <DialogContent className="sm:max-w-md [&>button]:hidden" onPointerDownOutside={(e) => e.preventDefault()}>
                <div className="flex flex-col items-center text-center py-6 space-y-6">
                  {/* Animated icon */}
                  <div className="relative">
                    <div className="w-20 h-20 rounded-2xl bg-seek/10 flex items-center justify-center animate-pulse">
                      {(() => {
                        const IconComp = parseMessages[parseMessageIdx]?.icon || Brain;
                        return <IconComp className="w-9 h-9 text-seek" />;
                      })()}
                    </div>
                    <div className="absolute -top-1 -end-1 w-6 h-6 rounded-full bg-seek flex items-center justify-center">
                      <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-1.5 min-h-[52px]">
                    <p className="text-base font-semibold text-foreground transition-all">
                      {parseMessages[parseMessageIdx]?.text}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {parseMessages[parseMessageIdx]?.sub}
                    </p>
                  </div>

                  {/* Progress bar */}
                  <div className="w-full space-y-2">
                    <Progress value={parseProgress} className="h-2.5 [&>[data-state]]:bg-seek" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{t('seek.profileImport.buildingProfile')}</span>
                      <span className="font-medium text-seek">{Math.round(parseProgress)}%</span>
                    </div>
                  </div>

                  {/* Fun fact / tip */}
                  <div className="rounded-lg bg-seek/5 border border-seek/10 px-4 py-2.5 w-full">
                    <p className="text-xs text-seek font-medium">
                      💡 {t('seek.profileImport.tip')}
                    </p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Overwrite Confirmation */}
            <AlertDialog open={confirmOverwrite} onOpenChange={setConfirmOverwrite}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    {t('seek.profileImport.replaceTitle')}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('seek.profileImport.replaceDesc')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => setPendingFile(null)}>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-seek hover:bg-seek/90 text-seek-foreground"
                    onClick={() => {
                      if (pendingFile) {
                        processUpload(pendingFile);
                        setPendingFile(null);
                      }
                      setConfirmOverwrite(false);
                    }}
                  >
                    {t('seek.profileImport.yesReplace')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 hover:bg-success hover:text-success-foreground hover:border-success"
              onClick={handleExportCv}
              disabled={exporting}
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {exporting ? t('seek.profile.exporting') : t('seek.profile.exportCv')}
            </Button>
          </div>
        </Card>

        {/* Resume CV Import banner */}
        {diffData && !diffPreviewOpen && (
          <div className="flex items-center justify-between rounded-lg border border-seek/30 bg-seek/5 px-4 py-3">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <FileText className="w-4 h-4 text-seek" />
              {t('seek.profileImport.pendingImport')}
            </div>
            <Button size="sm" className="gap-1.5 bg-seek hover:bg-seek/90 text-seek-foreground" onClick={() => setDiffPreviewOpen(true)}>
              {t('seek.profileImport.resumeImport')}
            </Button>
          </div>
        )}

        <div id="section-personal">
          <PersonalDetailsSection
            profile={profile}
            onSave={(data) => updateProfile.mutate(data)}
            saving={saving}
            completionScore={completionScore}
            missingItems={missingItems}
            editLocked={!!editingSection && editingSection !== 'personal'}
            onEditingChange={(active) => setEditingSection(active ? 'personal' : null)}
          />
        </div>

        {profileId && (
          <>
            <div id="section-skills">
              <SkillsSection
                skills={skills}
                profileId={profileId}
                onAdd={handleAddRecord('candidate_profile_skills')}
                onDelete={handleDeleteRecord('candidate_profile_skills')}
                editLocked={!!editingSection && editingSection !== 'skills'}
                onEditingChange={(active) => setEditingSection(active ? 'skills' : null)}
              />
            </div>

            <div id="section-experience">
              <ExperienceSection
                experiences={experiences}
                profileId={profileId}
                onAdd={handleAddRecord('candidate_profile_experiences')}
                onUpdate={handleUpdateRecord('candidate_profile_experiences')}
                onDelete={handleDeleteRecord('candidate_profile_experiences')}
                saving={saving}
                isFreshGraduate={!!profile?.is_fresh_graduate}
                onToggleFreshGraduate={(checked) => updateProfile.mutate({ is_fresh_graduate: checked })}
                editLocked={!!editingSection}
              />
            </div>

            <div id="section-education">
              <EducationSection
                education={education}
                profileId={profileId}
                onAdd={handleAddRecord('candidate_profile_education')}
                onUpdate={handleUpdateRecord('candidate_profile_education')}
                onDelete={handleDeleteRecord('candidate_profile_education')}
                saving={saving}
                editLocked={!!editingSection}
              />
            </div>

            <CertificationsSection
              certifications={certifications}
              profileId={profileId}
              onAdd={handleAddRecord('candidate_profile_certifications')}
              onUpdate={handleUpdateRecord('candidate_profile_certifications')}
              onDelete={handleDeleteRecord('candidate_profile_certifications')}
              saving={saving}
              editLocked={!!editingSection}
            />

            <div id="section-languages">
              <LanguagesSection
                languages={languages}
                profileId={profileId}
                onAdd={handleAddRecord('candidate_profile_languages')}
                onUpdate={handleUpdateRecord('candidate_profile_languages')}
                onDelete={handleDeleteRecord('candidate_profile_languages')}
                saving={saving}
                editLocked={!!editingSection}
              />
            </div>

            <LinksSection
              links={links}
              profileId={profileId}
              onAdd={handleAddRecord('candidate_profile_links')}
              onUpdate={handleUpdateRecord('candidate_profile_links')}
              onDelete={handleDeleteRecord('candidate_profile_links')}
              saving={saving}
              editLocked={!!editingSection}
            />

            <PreferencesSection
              profile={profile}
              onSave={(data) => updateProfile.mutate(data)}
              saving={saving}
              editLocked={!!editingSection && editingSection !== 'preferences'}
              onEditingChange={(active) => setEditingSection(active ? 'preferences' : null)}
            />
          </>
        )}
      </div>

      {/* CV Diff Preview Dialog */}
      {diffData && (
        <CvDiffPreview
          open={diffPreviewOpen}
          onClose={handleDiffClose}
          onDiscard={handleDiffDiscard}
          onApply={(editedData, appliedDecisions) => applyParsedData(editedData, appliedDecisions)}
          onFinishNoChanges={async () => {
            // User intentionally keeps all current values — complete session without applying
            await reviewSession.completeSession.mutateAsync().catch(() => {});
            setDiffPreviewOpen(false);
            setDiffData(null);
            setDiffDocId(null);
            toast.success('Review completed — no changes applied to your profile.');
          }}
          applying={applying}
          parsedData={diffData.parsed_data}
          currentData={{
            profile,
            experiences,
            education,
            skills,
            languages,
            certifications,
          }}
          validationIssues={diffData.validation_issues}
          extractionQuality={diffData.extraction_quality}
          confidence={diffData.confidence}
          decisions={profileDecisions}
          onUpdateFieldAction={reviewSession.updateFieldAction}
          onUpdateItemAction={reviewSession.updateItemAction}
          onUpdateAdditionalAction={reviewSession.updateAdditionalAction}
          isRecoveredSession={reviewSession.isRecoveredSession}
        />
      )}
    </SeekLayout>
  );
}
